<template>
  <div class="expandable-list-section good-to-know-section" :class="goodToKnowSectionClasses">
    <div class="claims good-to-know-section__claims">
      <div
        v-for="claim of productClaims"
        :key="claim"
        class="flex--column--align-center claim claims__claim"
      >
        <div class="icon claim__icon">{{ $options.claimIcons[claim] }}</div>
        <div class="claim__title">{{ $t($options.claimTitles[claim]) }}</div>
      </div>
    </div>
    <div v-if="hasNotes" class="notes good-to-know-section__notes">
      <div v-for="note of notes" :key="note" class="notes__note">{{ note }}</div>
    </div>
    <div v-if="hasWarningInfo" class="warnings good-to-know-section__warnings">
      <div v-for="warning of warningTitles" :key="warning" class="warnings__warning warning">
        <span class="warning__icon icon">{{ $options.warningIcon }}</span>
        <span class="warning__title">{{ $t(warning) }}</span>
      </div>
    </div>
    <div
      v-if="hasTipsAndTrics"
      class="expandable-list-section__paragraph tips-and-tricks good-to-know-section__tips-and-tricks"
    >
      <h2 class="expandable-list-section-paragraph__title">
        {{ $t('discover.expandableList.section.goodToKnow.tipsAndTricksLabel') }}
      </h2>
      <expandable-list-paragraph
        v-for="paragraph of tipsAndTricksParagraphs"
        :key="paragraph.body"
        class="tips-and-tricks__paragraph"
        :paragraph="paragraph"
      />
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'ramda';
import { mapGetters } from 'vuex';

import ExpandableListParagraph from '@/components/discover/ExpandableListParagraph.vue';

import { PRODUCT_NAMES } from '@/constants/treatment';
import { ICON, ICON_VALUE } from '@/constants/icons';
import { COUNTRY_ISO } from '@/constants/countries';

import { types } from '@/store/types';

import {
  CLAIM_TITLE,
  CLAIM_ICON_VALUE,
  PRODUCT_CLAIMS,
  PRODUCT_TIPS_AND_TRICKS,
  CLAIM_NOTE,
  WARNING_TITLES
} from '@/constants/discover';

export default {
  name: 'GoodToKnowSection',
  components: { ExpandableListParagraph },
  claimIcons: CLAIM_ICON_VALUE,
  warningIcon: ICON_VALUE[ICON.WARNING],
  claimTitles: CLAIM_TITLE,
  props: {
    productName: {
      type: String,
      default: ''
    },
    darkTheme: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      deliveryCountry: types.getters.DELIVERY_COUNTRY
    }),
    productClaims() {
      return PRODUCT_CLAIMS[this.productName];
    },
    tipsAndTricksParagraphs() {
      const paragraphs = PRODUCT_TIPS_AND_TRICKS[this.productName] || [];
      return Array.isArray(paragraphs) ? paragraphs : [paragraphs];
    },
    hasTipsAndTrics() {
      return !isEmpty(this.tipsAndTricksParagraphs);
    },
    hasNotes() {
      return !isEmpty(this.notes);
    },
    notes() {
      return this.productClaims.reduce((notes, claim) => {
        return CLAIM_NOTE[claim] ? [...notes, this.$t(CLAIM_NOTE[claim])] : notes;
      }, []);
    },
    goodToKnowSectionClasses() {
      return {
        'good-to-know-section--dark-theme': this.darkTheme
      };
    },
    warningTitles() {
      return WARNING_TITLES;
    },
    isSerumProduct() {
      return (
        this.productName === PRODUCT_NAMES.FACE_SERUM ||
        this.productName === PRODUCT_NAMES.EYE_CONTOUR_SERUM
      );
    },
    hasWarningInfo() {
      return (
        this.deliveryCountry.toLowerCase() === COUNTRY_ISO.AUSTRALIA.toLowerCase() &&
        this.isSerumProduct
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/icon.scss';
@import '../../assets/scss/colors.scss';
@import '../../assets/scss/expandable-list.scss';

.good-to-know-section {
  &--dark-theme {
    .claim__icon,
    .notes {
      color: $westar-color;
    }
  }

  &__claims {
    margin-bottom: 2rem;
  }

  &__notes {
    margin: 2rem 0.5rem 3rem 0.5rem;
  }

  &__tips-and-tricks {
    margin: 0 0.5rem 2rem 0.5rem;
  }
}

.tips-and-tricks {
  &__paragraph {
    padding-bottom: 10px;
  }
}

.claims {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.claim {
  margin: 1.4rem 0.5rem;
  width: 10rem;

  &__icon {
    margin-bottom: 2.8rem;
    font-size: 3rem;
    color: $concord-color;
  }

  &__title {
    font-size: 1.1rem;
    line-height: 1.2rem;
    font-weight: 600;
    text-align: center;
  }
}

.notes {
  color: $concord-color;
  font-size: 1.2rem;
  line-height: 1.5rem;
}

.notes:lang(ar) {
  font-size: 1.4rem;
  line-height: 1.7rem;
}

.warnings {
  margin-top: 34px;
  padding-left: 5px;
}

.warning {
  margin-bottom: 16px;

  &__icon {
    font-size: 20px;
  }

  &__title {
    font-size: 1.1rem;
    line-height: 1.2rem;
    font-weight: 600;
    margin-left: 10px;
  }
}

@media (min-width: 600px) {
  .claim {
    &__icon {
      margin-bottom: 3.5rem;
      font-size: 3.1rem;
    }

    &__title {
      font-size: 1.2rem;
      line-height: 1.4rem;
      font-weight: 600;
    }
  }

  .warning {
    &__title {
      font-size: 1.2rem;
      line-height: 1.4rem;
      font-weight: 600;
    }
  }
}

@media (min-width: 960px) {
  .claim {
    &__icon {
      font-size: 3.2rem;
    }
  }
}

@media (min-width: 1264px) {
  .claim {
    width: 12rem;

    &__icon {
      font-size: 3.3rem;
    }
  }
}

@media (min-width: 1920px) {
  .claim {
    &__icon {
      font-size: 3.4rem;
    }
  }
}
</style>
